import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  CheckCircle2Icon,
  MapPinIcon,
  PackageIcon,
  StoreIcon,
  TruckIcon,
} from '@/shared/ui/icons';
import { ClientStatusChip } from '@/shared/ui/chips/ClientStatusChip';
import { FeaturesSplitsStatusChip } from '@/features/splits';
import { formatUsd } from '@/shared/utils/currency';
import { formatDate } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';
import type { ApiParcelsGetItem } from '@/shared/types/api/parcels';

type ParcelCardProps = {
  parcel: ApiParcelsGetItem;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
};

export const FeaturesParcelsParcelCard = ({
  parcel,
  selectable = false,
  selected = false,
  onToggleSelect,
}: ParcelCardProps) => {
  const { t } = useTranslation();

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {selectable ? (
            <span
              className={cn(
                'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border',
                selected
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-slate-300 bg-white text-transparent',
              )}
            >
              <CheckCircle2Icon className="h-4 w-4" aria-hidden />
            </span>
          ) : (
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
              <PackageIcon className="h-4 w-4" aria-hidden />
            </span>
          )}
          <span className="truncate text-sm font-semibold text-ink">{parcel.name}</span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {!parcel.is_split && <ClientStatusChip status={parcel.client_status ?? 'IN_PROGRESS'} />}
          {parcel.is_split && parcel.split_status && (
            <FeaturesSplitsStatusChip status={parcel.split_status} />
          )}
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-1.5 text-xs text-ink-muted sm:grid-cols-2">
        <div className="flex items-center gap-1.5">
          <TruckIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="truncate">{t('parcels:trek', { trekId: parcel.trek_id })}</span>
        </div>
        {parcel.store_name && (
          <div className="flex items-center gap-1.5">
            <StoreIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">{parcel.store_name}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="truncate">{t(`regions:${parcel.region}`)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <CalendarDaysIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{formatDate(parcel.created_at)}</span>
        </div>
      </dl>

      <div className="flex items-end justify-between border-t border-slate-100 pt-2 text-sm">
        <span className="text-ink-muted">{t('common:total')}</span>
        <span className="font-semibold text-ink">{formatUsd(parcel.total_amount)}</span>
      </div>
    </>
  );

  if (selectable) {
    return (
      <button
        type="button"
        aria-pressed={selected}
        onClick={() => onToggleSelect?.(parcel.id)}
        className={cn(
          'flex w-full cursor-pointer flex-col gap-3 rounded-2xl border bg-white p-4 text-left shadow-card transition-colors',
          selected
            ? 'border-brand-400 bg-brand-50/50'
            : 'border-slate-200 hover:border-brand-200 hover:bg-brand-50/40',
        )}
      >
        {body}
      </button>
    );
  }

  return (
    <Link
      to={`/parcels/${parcel.id}`}
      className="group flex cursor-pointer flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-card transition-colors hover:border-brand-200 hover:bg-brand-50/40 active:bg-brand-50/60"
    >
      {body}
    </Link>
  );
};
